// Structured, per-edit correction parsing.
//
// Goal: instead of asking the model for one big "corrected text" blob, ask it
// for a list of discrete edits (original fragment -> replacement + short reason).
// This lets the UI show Grammarly-style per-fix accept/reject cards instead of
// an all-or-nothing replace.

export interface CorrectionEdit {
  /** Exact substring of the original text this edit replaces. Must appear verbatim in the input. */
  original: string;
  /** Suggested replacement for `original`. */
  replacement: string;
  /** Short reason a human can read at a glance, e.g. "subject-verb agreement", "typo". */
  reason: string;
  /** Coarse category, useful for icon/color coding in the UI. */
  category: 'spelling' | 'grammar' | 'punctuation' | 'style' | 'clarity';
}

export interface StructuredCorrectionResult {
  /** true if the model found no issues. */
  clean: boolean;
  edits: CorrectionEdit[];
}

/**
 * Prompt instructing the model to return ONLY a JSON object matching
 * StructuredCorrectionResult. Kept separate from aiService's existing free-text
 * correction prompt (still fine for translation/chat) since JSON output and
 * token-by-token streaming don't mix well — call this non-streaming.
 */
export const STRUCTURED_CORRECTION_PROMPT = `You are a precise grammar, spelling and punctuation checker.
Find every error in the text below and return ONLY a JSON object, no commentary, no markdown fences, matching exactly this shape:

{"clean": boolean, "edits": [{"original": string, "replacement": string, "reason": string, "category": "spelling"|"grammar"|"punctuation"|"style"|"clarity"}]}

Rules:
- "original" must be an exact, verbatim substring of the input text (same casing, same whitespace) so it can be located with a plain string search.
- Keep each "original" as short as possible: the smallest span that captures the error (a word or a few words), not the whole sentence.
- "reason" MUST be written in the same language as the input text (e.g. Dutch input -> Dutch reason, Arabic input -> Arabic reason). Never default to English unless the input itself is English. Keep it short (max ~8 words).
- If the text has no errors, return {"clean": true, "edits": []}.
- Do not invent stylistic rewrites unless something is actually wrong or unclear.
- Preserve the input's language — never translate.

Language-specific attention points (apply the relevant one based on the input's language):
- Arabic: pay close attention to hamza placement (ء أ إ آ), confusing ta marbuta with ha (ة vs ه), subject-verb-number-gender agreement, and common preposition errors. Do NOT add, remove, or change diacritics (tashkeel) that are not already present in "original" unless the input consistently uses full diacritics throughout — unrequested diacritic changes make "original" fail to match the source text exactly, silently dropping the edit.
- Dutch: pay close attention to de/het article agreement, werkwoordspelling (d/t, dt-fouten), and relative pronoun choice (die/dat).

Text:
"""
{text}
"""`;

/**
 * Parses a model response into a StructuredCorrectionResult, tolerating the
 * usual LLM quirks (markdown code fences, leading/trailing prose).
 * Throws if no valid JSON object can be recovered — callers should catch this
 * and fall back to treating the raw response as plain corrected text.
 */
export function parseStructuredCorrection(raw: string, originalText: string): StructuredCorrectionResult {
  const jsonSlice = extractJsonObject(raw);
  if (!jsonSlice) {
    throw new Error('No JSON object found in correction response');
  }

  let parsed: any;
  try {
    parsed = JSON.parse(jsonSlice);
  } catch (e) {
    throw new Error(`Failed to parse correction JSON: ${(e as Error).message}`);
  }

  if (typeof parsed !== 'object' || parsed === null || !Array.isArray(parsed.edits)) {
    throw new Error('Correction JSON missing "edits" array');
  }

  const validCategories = new Set(['spelling', 'grammar', 'punctuation', 'style', 'clarity']);

  const edits: CorrectionEdit[] = [];
  for (const e of parsed.edits) {
    if (!e || typeof e.original !== 'string' || typeof e.replacement !== 'string') continue;

    // Despite the "exact, verbatim substring" instruction, models sometimes still return
    // "original" with slightly different casing (e.g. capitalizing a word that's lowercase
    // mid-sentence in the actual text) or extra/missing surrounding whitespace. A strict
    // originalText.includes(e.original) silently drops those edits entirely - no error, just
    // nothing rendered, which is indistinguishable from "no issues found" in the UI. Locate the
    // real span case-insensitively (and with collapsed whitespace) as a fallback, but always
    // store the ACTUAL source substring as `original` so every downstream consumer
    // (CorrectionDiffView's locateEdits, applyEdits) keeps matching exactly - only how we find
    // the span is fuzzy, never what we apply.
    const idx = findFragment(originalText, e.original);
    if (idx === -1) continue; // genuinely not in the source text (paraphrase, hallucination) - drop it
    const actualOriginal = originalText.slice(idx, idx + e.original.length);

    edits.push({
      original: actualOriginal,
      replacement: e.replacement,
      reason: typeof e.reason === 'string' ? e.reason : '',
      category: validCategories.has(e.category) ? e.category : 'grammar',
    });
  }

  return {
    clean: edits.length === 0,
    edits,
  };
}

/**
 * Finds where `quoted` sits in `text`. Tries an exact match first (the common case), then
 * falls back to a case-insensitive search so minor casing drift from the model doesn't drop
 * an otherwise-valid edit. Returns -1 if it can't be found either way.
 */
function findFragment(text: string, quoted: string): number {
  const exactIdx = text.indexOf(quoted);
  if (exactIdx !== -1) return exactIdx;
  if (!quoted) return -1;
  return text.toLowerCase().indexOf(quoted.toLowerCase());
}

function extractJsonObject(raw: string): string | null {
  const fenced = raw.match(/```(?:json)?\s*([\s\S]*?)```/i);
  const candidate = fenced ? fenced[1] : raw;
  const start = candidate.indexOf('{');
  const end = candidate.lastIndexOf('}');
  if (start === -1 || end === -1 || end < start) return null;
  return candidate.slice(start, end + 1);
}

/**
 * Applies a subset of edits (the ones the user accepted) to the original text.
 * Edits are applied left-to-right; each `original` is matched against the
 * first not-yet-consumed occurrence, so repeated fragments behave predictably.
 */
export function applyEdits(originalText: string, edits: CorrectionEdit[]): string {
  let result = originalText;
  let searchFrom = 0;
  for (const edit of edits) {
    const idx = result.indexOf(edit.original, searchFrom);
    if (idx === -1) continue; // already consumed or not found; skip rather than throw
    result = result.slice(0, idx) + edit.replacement + result.slice(idx + edit.original.length);
    searchFrom = idx + edit.replacement.length;
  }
  return result;
}
